import {
  defaultPrinterSettings,
  electronPrintSettingsSlice,
  printJobSignature,
} from '@store/slices/electronPrintSettings';
import { useAppDispatch, useAppSelector } from '..';
import { ElectronPrintSettings } from '@interfaces/general';

export const useElectronPrintSettingsFromStore = () => {
  return useAppSelector((state) => state.electronPrintSettings);
};

// Impresora "principal" para acciones de impresión MANUAL (botones de imprimir
// pedido) y para flags de auto-print de pantalla. Reemplaza el acceso fijo a
// `.Printer1`, que se rompía con el modelo flexible al quitar esa impresora.
// Preferencia: primera habilitada con impresora asignada → primera habilitada →
// primera de la lista. Nunca undefined (el store garantiza ≥1; default defensivo).
export const useMainPrinter = (): ElectronPrintSettings => {
  const all = useElectronPrintSettingsFromStore();
  const keys = Object.keys(all);
  if (!keys.length) return defaultPrinterSettings();
  const withPrinter = keys.find((k) => all[k].electronEnabled !== false && all[k].electronPrinterName);
  const enabled = keys.find((k) => all[k].electronEnabled !== false);
  return all[withPrinter || enabled || keys[0]];
};

// Selección PURA de impresoras a usar al REIMPRIMIR: la principal SIEMPRE (preserva el
// comportamiento legacy) + cualquier otra con el opt-in `electronReprintEnabled`,
// deduplicada por key. Si nadie activa el flag, devuelve solo [main] → idéntico al
// comportamiento actual. Pura (sin hooks) para poder testearla sin renderHook/Provider.
export const selectReprintPrinters = (
  all: Record<string, ElectronPrintSettings>,
  main: ElectronPrintSettings,
): ElectronPrintSettings[] => {
  const keys = Object.keys(all);
  const mainKey = keys.find((k) => all[k] === main);
  const extras = keys
    .filter((k) => k !== mainKey)
    .map((k) => all[k])
    .filter((s) => s.electronEnabled !== false && s.electronPrinterName && s.electronReprintEnabled === true);
  return [main, ...extras];
};

// Impresoras a usar al reimprimir un ticket (botones "Reimprimir" de Orders). Wrapper
// delgado sobre selectReprintPrinters.
export const useReprintPrinters = (): ElectronPrintSettings[] => {
  const all = useElectronPrintSettingsFromStore();
  const main = useMainPrinter();
  return selectReprintPrinters(all, main);
};

// Selección PURA de impresoras para AUTO-PRINT de un modo dado (onOrderReceived /
// onOrderAccepted), deduplicada por printJobSignature. Colapsa entradas que producirían
// papel IDÉNTICO en la misma impresora física —raíz del bug de doble impresión: dos llaves
// del Record apuntando a la misma impresora con la misma config de output—. Preserva el
// multi-ticket legítimo (caja `normal` + cocina `simplified` en la misma impresora → firmas
// distintas → ambas sobreviven). Filtra pausadas (electronEnabled===false) y sin device.
// Conserva el electronPrintNumber de la entrada superviviente (no suma copias). Pura.
export const selectAutoPrintPrinters = (
  all: Record<string, ElectronPrintSettings>,
  mode: string,
): ElectronPrintSettings[] => {
  const seen = new Set<string>();
  const result: ElectronPrintSettings[] = [];
  Object.keys(all).forEach((key) => {
    const s = all[key];
    if (s.electronEnabled === false || !s.electronPrinterName || s.electronAutoPrint !== mode) return;
    const sig = printJobSignature(s);
    if (seen.has(sig)) return;
    seen.add(sig);
    result.push(s);
  });
  return result;
};

// Ventana de deduplicación de auto-print por pedido. Un mismo evento NEW_ORDER entregado dos
// veces por el socket (reconexión, doble emisión del backend) no debe auto-imprimir dos veces.
export const AUTO_PRINT_DEDUPE_WINDOW_MS = 30_000;

// Idempotencia PURA de auto-print por pedido: devuelve true si `orderId` ya se auto-imprimió
// dentro de la ventana (→ saltar la impresión de ESTE evento). Marca el id como visto y limpia
// entradas viejas in-place para no crecer sin límite. Se evalúa UNA vez por evento (no por
// bloque), de modo que un setup legítimo onOrderAccepted + onOrderReceived sigue imprimiendo en
// ambos dentro del MISMO evento. No afecta el botón manual de aceptar/reimprimir (otra ruta).
export const shouldSkipAutoPrint = (
  seen: Map<string, number>,
  orderId: string,
  now: number,
  windowMs: number = AUTO_PRINT_DEDUPE_WINDOW_MS,
): boolean => {
  seen.forEach((ts, id) => {
    if (now - ts > windowMs) seen.delete(id);
  });
  const prev = seen.get(orderId);
  if (prev !== undefined && now - prev <= windowMs) return true;
  seen.set(orderId, now);
  return false;
};

export const useElectronPrintSettingsActions = () => {
  const dispatch = useAppDispatch();

  // printerKey es un id dinámico (modelo flexible). V1 sigue pasando 'Printer1'…
  // que son strings → compatible.
  const updateElectronPrintSettings = (settings: Partial<ElectronPrintSettings>, printerKey: string) =>
    dispatch(electronPrintSettingsSlice.actions.updateElectronPrintSettings({ settings, printerKey }));

  const addPrinter = (patch?: Partial<ElectronPrintSettings>) =>
    dispatch(electronPrintSettingsSlice.actions.addPrinter(patch ? { patch } : undefined));

  const removePrinter = (printerKey: string) =>
    dispatch(electronPrintSettingsSlice.actions.removePrinter({ printerKey }));

  const resetPrinters = () => dispatch(electronPrintSettingsSlice.actions.reset());

  return { updateElectronPrintSettings, addPrinter, removePrinter, resetPrinters };
};
